import router from '@/router';
import { getInjectedJsonOrFail } from '@/services/get-injected-json';
import { enableCache, getCache, serialize, setCache } from '@/services/local-storage';
import { arrayAdd, arrayContains, arrayRemove } from '@/utilities/array';
import clientOnly from '@/utilities/client-only';
import { GRAPHICS_CATEGORY_ID, TEMPLATE_DIFFICULTY_LEVELS } from '@/utilities/consts';
import { mapSimpleGetters } from '@/utilities/vuex-helpers';
import analyticsEvents from '@/utils/analyticsEvents';
import { eventTracker } from '@/utils/eventTracker';
import Vue from 'vue';
import { SEARCH_TYPES } from '../../constants';
import { getProducts } from '../../services/browse-api';
import {
  CATEGORY_VERSION_BY_SLUG,
  DEFAULT_CATEGORY_SLUG,
  FPS_BY_SLUG,
  ORIENTATION_BY_SLUG,
  RESOLUTION_BY_SLUG,
  editorials,
  freshCategoryFilters,
  people,
} from './category-repo';
import {
  DATE_ADDED_DEFAULT_VALUE,
  DATE_ADDED_HEADER_LABELS,
  SORT_BY_DEFAULT_VALUE,
  SORT_BY_PEOPLE_I_FOLLOW,
  SORT_BY_RECENTLY_VIEWED,
  validateDateAdded,
  validateSortBy,
} from './filter-meta';
import { isValidBPM, prepareDuration } from './numeric-input-helper';
import { stateToApiRequest } from './state-to-api-request';
import { stateToBrowseEventProperties } from './state-to-browse-event-properties';
import stateToUrl from './state-to-url';
import {
  categoryEditorialBySlugOrFail,
  categoryFpsBySlugOrFail,
  categoryOrientationBySlugOrFail,
  categoryPeopleBySlugOrFail,
  categoryResolutionBySlugOrFail,
  categoryVersionBySlugOrFail,
  isValidSubCategorySlug,
  validateCategoryHasBpms,
  validateCategoryHasDurations,
  validateSlug,
  validateCategoryHasResolutions,
  validateCategoryHasVersions,
  validateTemplateDifficultyLevel,
} from './store-validators';
import urlToState from './url-to-state';
import { showTemplateDifficultyLevelFilter } from '@/utilities/browse-helper';

// Enable/disable browse cache
const isCacheEnabled = getInjectedJsonOrFail('browse_cache_enabled');
enableCache(isCacheEnabled);

export default {
  // should only be referenced inside mutations, actions, and getters
  state: {
    sortBy: SORT_BY_DEFAULT_VALUE,
    dateAdded: DATE_ADDED_DEFAULT_VALUE,
    isFree: false,

    categoryFilters: {},

    searchTags: [],
    optionalWords: [],
    excludeProductIds: [],
    searchEventTriggerSource: null,
    searchEventLocationSource: null,

    products: null,

    loading: false,

    productsCurrentPage: 1,
    productsPerPage: 60,
    productsLastPage: 10,
    productsTotalResults: 100,

    userLoggedIn: false,

    siteTotalProductsCount: 0,
    loginRegisterModalActive: false,

    page: '',

    noResultSubsequentSearchCount: 0,
  },
  // should only be referenced inside actions
  mutations: {
    setSearchEventTriggerSource(state, source) {
      state.searchEventTriggerSource = source;
    },
    setSearchEventLocationSource(state, source) {
      state.searchEventLocationSource = source;
    },
    setSiteTotalProductsCount(state, count) {
      state.siteTotalProductsCount = count;
    },
    setSortBy(state, slug) {
      validateSortBy(slug);
      state.sortBy = slug;
    },

    setDateAdded(state, slug) {
      validateDateAdded(slug);
      state.dateAdded = slug;
    },

    setIsFree(state, isFree) {
      state.isFree = isFree;
    },

    setTemplateDifficultyLevel(state, templateDifficultyLevel) {
      validateTemplateDifficultyLevel({ state, templateDifficultyLevel });
      state.categoryFilters.templateDifficultyLevel = templateDifficultyLevel;
    },

    setIsVector(state, isVector) {
      state.categoryFilters.isVector = isVector;
    },

    setIsIsolated(state, isIsolated) {
      state.categoryFilters.isIsolated = isIsolated;
    },

    addSubCategorySlug(state, slug) {
      validateSlug({ state, slug, category: 'subCategories' });
      state.categoryFilters.subCategories[slug].selected = true;
    },

    removeSubCategorySlug(state, slug) {
      validateSlug({ state, slug, category: 'subCategories' });
      state.categoryFilters.subCategories[slug].selected = false;
    },

    addFileTypeSlug(state, slug) {
      validateSlug({ state, slug, category: 'fileTypes' });
      state.categoryFilters.fileTypes[slug].selected = true;
    },

    removeFileTypeSlug(state, slug) {
      validateSlug({ state, slug, category: 'fileTypes' });
      state.categoryFilters.fileTypes[slug].selected = false;
    },

    addApplicationSupportedSlug(state, slug) {
      validateSlug({ state, slug, category: 'applicationsSupported' });
      state.categoryFilters.applicationsSupported[slug].selected = true;
    },

    removeApplicationSupportedSlug(state, slug) {
      validateSlug({ state, slug, category: 'applicationsSupported' });
      state.categoryFilters.applicationsSupported[slug].selected = false;
    },

    addThemeSlug(state, slug) {
      validateSlug({ state, slug, category: 'themes' });
      state.categoryFilters.themes[slug].selected = true;
    },

    removeThemeSlug(state, slug) {
      validateSlug({ state, slug, category: 'themes' });
      state.categoryFilters.themes[slug].selected = false;
    },

    addArtStyleSlug(state, slug) {
      validateSlug({ state, slug, category: 'artStyles' });
      state.categoryFilters.artStyles[slug].selected = true;
    },

    removeArtStyleSlug(state, slug) {
      validateSlug({ state, slug, category: 'artStyles' });
      state.categoryFilters.artStyles[slug].selected = false;
    },

    setCategoryPeopleSlug(state, slug) {
      const p = categoryPeopleBySlugOrFail({ state, slug });
      state.categoryFilters.people = p;
    },

    setCategoryFpsSlug(state, slug) {
      const fps = categoryFpsBySlugOrFail({ state, slug });
      state.categoryFilters.fps = fps;
    },

    removeCategoryResolution(state, slug) {
      const resolution = categoryResolutionBySlugOrFail({ state, slug });
      const category = state.categoryFilters;
      arrayRemove(category.resolution, resolution.slug);
    },

    addCategoryResolution(state, slug) {
      const resolution = categoryResolutionBySlugOrFail({ state, slug });
      const category = state.categoryFilters;
      arrayAdd(category.resolution, resolution.slug);
    },

    setCategoryResolution(state, values) {
      validateCategoryHasResolutions({ state });
      const category = state.categoryFilters;
      category.resolution = values;
    },

    removeCategoryOrientation(state, slug) {
      const orientation = categoryOrientationBySlugOrFail({ state, slug });
      const category = state.categoryFilters;
      arrayRemove(category.orientation, orientation.slug);
    },

    addCategoryOrientation(state, slug) {
      const orientation = categoryOrientationBySlugOrFail({ state, slug });
      const category = state.categoryFilters;
      arrayAdd(category.orientation, orientation.slug);
    },

    setCategoryOrientation(state, values) {
      validateCategoryHasResolutions({ state });
      const category = state.categoryFilters;
      category.orientation = values;
    },

    setCategoryProAffiliationValue(state, proAffiliated) {
      state.categoryFilters.proAffiliated = parseInt(proAffiliated, 10);
    },

    setCategoryRightsManagerValue(state, rightsManager) {
      state.categoryFilters.rightsManager = parseInt(rightsManager, 10);
    },

    setCategoryEditorialSlug(state, slug) {
      const editorial = categoryEditorialBySlugOrFail({ state, slug });
      state.categoryFilters.editorial = editorial;
    },

    removeCategoryVersion(state, slug) {
      const version = categoryVersionBySlugOrFail({ state, slug });
      const category = state.categoryFilters;
      arrayRemove(category.version, version.slug);
    },

    addCategoryVersion(state, slug) {
      const version = categoryVersionBySlugOrFail({ state, slug });
      const category = state.categoryFilters;
      arrayAdd(category.version, version.slug);
    },

    setCategoryVersion(state, values) {
      validateCategoryHasVersions({ state });
      const category = state.categoryFilters;
      category.version = values;
    },

    removeCategoryBpm(state, value) {
      validateCategoryHasBpms({ state });
      const category = state.categoryFilters;
      arrayRemove(category.bpms, value);
    },

    addCategoryBpm(state, value) {
      validateCategoryHasBpms({ state });
      const category = state.categoryFilters;
      arrayAdd(category.bpms, value);
    },

    setCategoryBpms(state, values) {
      validateCategoryHasBpms({ state });
      const category = state.categoryFilters;
      category.bpms = values;
    },

    removeCategoryDuration(state, value) {
      validateCategoryHasDurations({ state });
      const category = state.categoryFilters;
      arrayRemove(category.durations, value);
    },

    addCategoryDuration(state, value) {
      validateCategoryHasDurations({ state });
      const category = state.categoryFilters;
      arrayAdd(category.durations, value);
    },

    setCategoryDurations(state, values) {
      validateCategoryHasBpms({ state });
      const category = state.categoryFilters;
      category.durations = values;
    },

    setExcludeProductIds(state, productIds) {
      state.excludeProductIds = productIds;
    },

    setProducts(state, { products, currentPage, lastPage, totalResults, perPage }) {
      state.products = products;
      state.productsCurrentPage = parseInt(currentPage, 10);
      state.productsLastPage = parseInt(lastPage, 10);
      state.productsTotalResults = parseInt(totalResults, 10);
      state.productsPerPage = parseInt(perPage, 10);
    },

    setProductsFromApi(state, { products, currentPage, lastPage, totalResults, perPage }) {
      state.products = products;
      state.productsCurrentPage = parseInt(currentPage, 10);
      state.productsLastPage = parseInt(lastPage, 10);
      state.productsTotalResults = parseInt(totalResults, 10);
      state.productsPerPage = parseInt(perPage, 10);
    },

    setProductsCurrentPage(state, currentPage) {
      state.productsCurrentPage = parseInt(currentPage, 10);
    },

    setProductsPerPage(state, perPage) {
      state.productsPerPage = parseInt(perPage, 10);
    },

    addSearchTag(state, value) {
      arrayAdd(state.searchTags, value);
    },

    setSearchTags(state, value) {
      // remove duplicates
      value.forEach(_value => {
        arrayAdd(state.searchTags, _value);
      });
    },

    removeSearchTag(state, value) {
      arrayRemove(state.searchTags, value);
    },

    clearFilterTags(state) {
      state.categoryFilters = freshCategoryFilters(state.categoryFilters.slug);
      state.isFree = false;
      state.searchTags = [];
      state.excludeProductIds = [];
      state.optionalWords = [];
      state.productsCurrentPage = 1;
    },

    setUserMeta(state, { userLoggedIn }) {
      state.userLoggedIn = userLoggedIn;
    },

    setFilterDefaults(state, categorySlug) {
      state.sortBy = SORT_BY_DEFAULT_VALUE;
      state.dateAdded = DATE_ADDED_DEFAULT_VALUE;
      state.categoryFilters = freshCategoryFilters(categorySlug);
      state.searchTags = [];
      state.excludeProductIds = [];
      state.optionalWords = [];
      state.productsCurrentPage = 1;
    },

    setOptionalWords(state, optionalWords) {
      state.optionalWords = optionalWords;
    },

    setLoading(state, value) {
      state.loading = value;
    },

    setLoginRegisterModalActive(state, value) {
      state.loginRegisterModalActive = value;
    },

    setPage(state, value) {
      state.page = value;
    },

    setNoResultSubsequentSearchCount(state, value) {
      state.noResultSubsequentSearchCount = value;
    },
  },
  actions: {
    setSortBy({ commit, dispatch, getters }, slug) {
      const loginRequired = [SORT_BY_PEOPLE_I_FOLLOW, SORT_BY_RECENTLY_VIEWED];

      if (!getters.isLoggedIn && arrayContains(loginRequired, slug)) {
        commit('setLoginRegisterModalActive', true);
        return Promise.resolve();
      }
      commit('setSortBy', slug);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter', type: 'sortBy' });
    },

    setDateAdded({ commit, dispatch }, slug) {
      commit('setDateAdded', slug);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setIsFree({ commit, dispatch }, isFree) {
      commit('setIsFree', isFree);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setTemplateDifficultyLevel({ commit, dispatch }, templateDifficultyLevel) {
      commit('setTemplateDifficultyLevel', templateDifficultyLevel);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setIsVector({ commit, dispatch }, isVector) {
      commit('setIsVector', isVector);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setIsIsolated({ commit, dispatch }, isIsolated) {
      commit('setIsIsolated', isIsolated);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setCategory({ commit, dispatch }, { categorySlug, value }) {
      if (value) {
        commit('addCategorySlug', categorySlug);
      } else {
        commit('removeCategorySlug', categorySlug);
      }

      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setSubCategory({ commit, dispatch }, { slug, checked }) {
      if (checked) {
        commit('addSubCategorySlug', slug);
      } else {
        commit('removeSubCategorySlug', slug);
      }
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setFileType({ commit, dispatch }, { slug, checked }) {
      if (checked) {
        commit('addFileTypeSlug', slug);
      } else {
        commit('removeFileTypeSlug', slug);
      }
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setApplicationSupported({ commit, dispatch }, { slug, checked }) {
      if (checked) {
        commit('addApplicationSupportedSlug', slug);
      } else {
        commit('removeApplicationSupportedSlug', slug);
      }
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setTheme({ commit, dispatch }, { slug, checked }) {
      if (checked) {
        commit('addThemeSlug', slug);
      } else {
        commit('removeThemeSlug', slug);
      }
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setArtStyle({ commit, dispatch }, { slug, checked }) {
      if (checked) {
        commit('addArtStyleSlug', slug);
      } else {
        commit('removeArtStyleSlug', slug);
      }
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setCategoryPeople({ commit, dispatch }, slug) {
      commit('setCategoryPeopleSlug', slug);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setCategoryFps({ commit, dispatch }, slug) {
      commit('setCategoryFpsSlug', slug);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setCategoryResolution({ commit, dispatch }, values) {
      commit('setCategoryResolution', values);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    removeCategoryResolution({ state, commit, dispatch }, value) {
      const category = state.categoryFilters;

      if (!arrayContains(category.resolution, value)) {
        return Promise.resolve();
      }

      commit('removeCategoryResolution', value);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    addCategoryResolution({ state, commit, dispatch }, value) {
      const category = state.categoryFilters;

      if (arrayContains(category.resolution, value)) {
        return Promise.resolve();
      }

      commit('addCategoryResolution', value);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setCategoryOrientation({ commit, dispatch }, values) {
      commit('setCategoryOrientation', values);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    removeCategoryOrientation({ state, commit, dispatch }, value) {
      const category = state.categoryFilters;

      if (!arrayContains(category.orientation, value)) {
        return Promise.resolve();
      }

      commit('removeCategoryOrientation', value);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    addCategoryOrientation({ state, commit, dispatch }, value) {
      const category = state.categoryFilters;

      if (arrayContains(category.orientation, value)) {
        return Promise.resolve();
      }

      commit('addCategoryOrientation', value);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setCategoryProAffiliation({ commit, dispatch }, proAffiliated) {
      commit('setCategoryProAffiliationValue', proAffiliated);

      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setCategoryRightsManager({ commit, dispatch }, rightsManager) {
      commit('setCategoryRightsManagerValue', rightsManager);

      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setCategoryVersion({ commit, dispatch }, values) {
      commit('setCategoryVersion', values);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    removeCategoryVersion({ state, commit, dispatch }, value) {
      const category = state.categoryFilters;

      if (!arrayContains(category.version, value)) {
        return Promise.resolve();
      }

      commit('removeCategoryVersion', value);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    addCategoryVersion({ state, commit, dispatch }, value) {
      const category = state.categoryFilters;

      if (arrayContains(category.version, value)) {
        return Promise.resolve();
      }

      commit('addCategoryVersion', value);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    setCategoryEditorial({ commit, dispatch }, editorialSlug) {
      commit('setCategoryEditorialSlug', editorialSlug);

      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    removeCategoryBpm({ state, commit, dispatch }, value) {
      validateCategoryHasBpms({ state });
      const category = state.categoryFilters;

      if (!arrayContains(category.bpms, value)) {
        return Promise.resolve();
      }

      commit('removeCategoryBpm', value);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    addCategoryBpm({ state, commit, dispatch }, value) {
      validateCategoryHasBpms({ state });
      const category = state.categoryFilters;

      if (arrayContains(category.bpms, value)) {
        return Promise.resolve();
      }

      if (!isValidBPM(value)) {
        return Promise.resolve();
      }

      commit('addCategoryBpm', value);
      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    removeCategoryDuration({ state, commit, dispatch }, value) {
      validateCategoryHasDurations({ state });
      const category = state.categoryFilters;

      // eslint-disable-next-line no-param-reassign
      value = prepareDuration(value);

      const invalid = value === false;

      if (invalid) {
        return Promise.resolve();
      }

      if (!arrayContains(category.durations, value)) {
        return Promise.resolve();
      }

      commit('removeCategoryDuration', value);

      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    addCategoryDuration({ state, commit, dispatch }, value) {
      validateCategoryHasDurations({ state });
      const category = state.categoryFilters;

      // eslint-disable-next-line no-param-reassign
      value = prepareDuration(value);

      const invalid = value === false;

      if (invalid) {
        return Promise.resolve();
      }

      if (arrayContains(category.durations, value)) {
        return Promise.resolve();
      }

      commit('addCategoryDuration', value);

      return dispatch('filterChange', { searchEventTriggerSource: 'filter' });
    },

    addSearchTag({ state, commit, dispatch }, value) {
      // eslint-disable-next-line no-param-reassign
      value = value.trim();

      if (state.searchTags.length >= 12) {
        return Promise.resolve();
      }

      if (arrayContains(state.searchTags, value)) {
        return Promise.resolve();
      }

      commit('addSearchTag', value);
      commit('setSearchEventLocationSource', 'marketplace');

      return dispatch('filterChange', {
        removeSearchHeaderParam: true,
        searchEventTriggerSource: 'free text search',
      });
    },

    removeSearchTag({ state, commit, dispatch }, value) {
      // eslint-disable-next-line no-param-reassign
      value = value.trim();

      if (!arrayContains(state.searchTags, value)) {
        return Promise.resolve();
      }

      commit('removeSearchTag', value);

      return dispatch('filterChange', {
        removeSearchHeaderParam: true,
        searchEventTriggerSource: 'remove search term',
      });
    },

    clearFilterTags({ commit, dispatch }) {
      commit('clearFilterTags');

      return dispatch('filterChange', {
        removeSearchHeaderParam: true,
        searchEventTriggerSource: 'clear search terms',
      });
    },

    setProducts({ commit, dispatch }, { products, currentPage, lastPage, totalResults }) {
      commit('setProducts', {
        products,
        currentPage,
        lastPage,
        totalResults,
      });
      commit('setSearchEventLocationSource', null); // reset location source after search
      return dispatch('productsChange', products);
    },

    setProductsFromApi({ commit }, { products, currentPage, lastPage, totalResults }) {
      commit('setProductsFromApi', {
        products,
        currentPage,
        lastPage,
        totalResults,
      });
      return Promise.resolve();
    },

    setProductsCurrentPage({ dispatch }, currentPage) {
      return dispatch('filterChange', {
        newCurrentPage: currentPage,
        fromPagination: true,
        searchEventTriggerSource: 'set products current page',
      });
    },

    setProductsPerPage({ commit, dispatch }, perPage) {
      commit('setProductsPerPage', perPage);
      return dispatch('filterChange', { searchEventTriggerSource: 'set products per page' });
    },

    setUserMeta({ commit }, { userLoggedIn }) {
      commit('setUserMeta', { userLoggedIn });
      return Promise.resolve();
    },

    setOptionalWords({ commit, dispatch }, optionalWords) {
      commit('setOptionalWords', optionalWords);
      return dispatch('filterChange', { searchEventTriggerSource: 'set optional words' });
    },

    setLoginRegisterModalActive({ commit }, value) {
      commit('setLoginRegisterModalActive', value);
      return Promise.resolve();
    },

    setStateFromRoute({ commit, dispatch }, route) {
      commit('setNoResultSubsequentSearchCount', 0);
      let categorySlug = route.params.category;
      let subCategorySlug = route.params.subCategory;
      if (categorySlug === 'free') {
        categorySlug = DEFAULT_CATEGORY_SLUG;
        subCategorySlug = 'free';
      }
      return dispatch('setStateFromUrl', {
        categorySlug,
        subCategorySlug,
        url: route.fullPath,
      });
    },

    setStateFromUrl({ commit, dispatch, state }, { categorySlug, subCategorySlug, url }) {
      const newState = urlToState(url, categorySlug);

      commit('setFilterDefaults', categorySlug);
      commit('setIsFree', newState.isFree);

      if (state.productsCurrentPage !== newState.productsCurrentPage) {
        commit('setProductsCurrentPage', newState.productsCurrentPage);
      }

      if (state.dateAdded !== newState.dateAdded) {
        commit('setDateAdded', newState.dateAdded);
      }

      if (state.sortBy !== newState.sortBy) {
        commit('setSortBy', newState.sortBy);
      }
      if (newState.searchTags.length) {
        commit('setSearchTags', newState.searchTags);
      }

      const category = state.categoryFilters;
      const newCategory = newState.categoryFilters;
      const newSubCategories = newCategory.subCategories || {};
      const newFileTypes = newCategory.fileTypes || {};
      const newApplicationsSupported = newCategory.applicationsSupported || {};
      const newThemes = newCategory.themes || {};
      const newArtStyles = newCategory.artStyles || {};

      if (newCategory.hasBpms) {
        if (newCategory.bpms.length) {
          commit('setCategoryBpms', newCategory.bpms);
        }
        if (newCategory.durations.length) {
          commit('setCategoryDurations', newCategory.durations);
        }
        if (category.proAffiliated !== newCategory.proAffiliated) {
          commit('setCategoryProAffiliationValue', newCategory.proAffiliated);
        }
      }

      if (newCategory.hasRightsManagers) {
        if (category.rightsManager !== newCategory.rightsManager) {
          commit('setCategoryRightsManagerValue', newCategory.rightsManager);
        }
      }

      if (newCategory.hasVersions) {
        if (newCategory.version.length > 0) {
          commit('setCategoryVersion', newCategory.version);
        }
      }

      if (newCategory.hasEditorials) {
        if (category.editorial.slug !== newCategory.editorial.slug) {
          commit('setCategoryEditorialSlug', newCategory.editorial.slug);
        }
      }

      if (newCategory.id === GRAPHICS_CATEGORY_ID) {
        commit('setIsVector', newCategory.isVector);
        commit('setIsIsolated', newCategory.isIsolated);

        Object.values(category.fileTypes).forEach(fileType => {
          const newFileType = newFileTypes[fileType.slug] || {};
          if (newFileType.selected) {
            commit('addFileTypeSlug', fileType.slug);
          }
        });

        Object.values(category.applicationsSupported).forEach(application => {
          const newApplication = newApplicationsSupported[application.slug] || {};
          if (newApplication.selected) {
            commit('addApplicationSupportedSlug', application.slug);
          }
        });

        Object.values(category.themes).forEach(theme => {
          const newTheme = newThemes[theme.slug] || {};
          if (newTheme.selected) {
            commit('addThemeSlug', theme.slug);
          }
        });

        Object.values(category.artStyles).forEach(artStyle => {
          const newArtStyle = newArtStyles[artStyle.slug] || {};

          if (newArtStyle.selected) {
            commit('addArtStyleSlug', artStyle.slug);
          }
        });

        if (category.people.slug !== newCategory.people.slug) {
          commit('setCategoryPeopleSlug', newCategory.people.slug);
        }
      }

      if (newCategory.hasFps) {
        if (category.fps.slug !== newCategory.fps.slug) {
          commit('setCategoryFpsSlug', newCategory.fps.slug);
        }
      }

      if (newCategory.hasResolutions) {
        if (newCategory.resolution.length > 0) {
          commit('setCategoryResolution', newCategory.resolution);
        }

        if (newCategory.orientation.length > 0) {
          commit('setCategoryOrientation', newCategory.orientation);
        }
      }

      if (
        newCategory.id === GRAPHICS_CATEGORY_ID &&
        newCategory.orientation.length > 0
      ) {
        commit('setCategoryOrientation', newCategory.orientation);
      }

      Object.values(category.subCategories).forEach(subCategory => {
        const newSubCategory = newSubCategories[subCategory.slug] || {};
        if (newSubCategory.selected) {
          commit('addSubCategorySlug', subCategory.slug);
        }
      });

      if (categorySlug === 'free' || subCategorySlug === 'free' || newState.isFree === true) {
        commit('setIsFree', true);
      }

      if (isValidSubCategorySlug({ state, slug: subCategorySlug })) {
        commit('addSubCategorySlug', subCategorySlug);
      } 
      if (subCategorySlug === 'easy-to-use' || newCategory.templateDifficultyLevel) {
        const addTemplateDifficultyLevelFilter = showTemplateDifficultyLevelFilter(category.id);
        if (addTemplateDifficultyLevelFilter) {
          commit('setTemplateDifficultyLevel', newCategory.templateDifficultyLevel || TEMPLATE_DIFFICULTY_LEVELS.SIMPLE);
        }
      }

      return dispatch('fetch');
    },

    fetch({ commit, state, dispatch, rootState }) {
      if (router.currentRoute.query.search_header) {
        commit('setSearchEventTriggerSource', 'click through from header search');
      }

      commit('setProductsPerPage', state.perPage);

      const request = stateToApiRequest(state);
      // this is also checked for back end caching.
      // See: laravel/app/Services/Algolia/AlgoliaSearchRequest.php
      const hasFollow = arrayContains(request.filters, SORT_BY_PEOPLE_I_FOLLOW);
      const hasRecentlyViewed = arrayContains(request.filters, SORT_BY_RECENTLY_VIEWED);
      const hasUserSpecificFilters = hasFollow || hasRecentlyViewed;

      let cacheKey = serialize(request);

      // Load products from client side cache if possible.
      if (!hasUserSpecificFilters) {
        if (!cacheKey || cacheKey === '/') {
          cacheKey = window.location.pathname;
        }
        const cached = getCache(cacheKey);
        if (cached) {
          commit('setLoading', false);
          // only track the original search, not the automatic subsequent ones
          if (state.noResultSubsequentSearchCount === 0) {
            eventTracker(analyticsEvents.numberOfSearchResultsReturned, {
              ...stateToBrowseEventProperties(state),
              number_of_search_results: cached.totalResults,
            });
          }

          return dispatch('setProducts', cached);
        }
      }

      // If we can't load products from cache, load from server side api.
      commit('setLoading', true);
      return getProducts(request)
        .then(({ products, currentPage, lastPage, totalResults, perPage, optionalWords }) => {
          if (!hasUserSpecificFilters) {
            setCache(cacheKey, {
              products,
              currentPage,
              lastPage,
              totalResults,
              perPage,
              optionalWords,
            });
          }
          // only track the original search, not the automatic subsequent ones
          clientOnly(() => {
            if (state.noResultSubsequentSearchCount === 0) {
              eventTracker(analyticsEvents.numberOfSearchResultsReturned, {
                ...stateToBrowseEventProperties(state),
                number_of_search_results: totalResults,
              });
            }
          });
          dispatch('setProducts', {
            products,
            currentPage,
            lastPage,
            totalResults,
            perPage,
            optionalWords,
          });
          commit('setLoading', false);
        })
        .catch(() => {
          commit('setLoading', false);
          // @TODO notify user of error
        });
    },
    filterChange({ state, commit, dispatch }, args) {
      commit('setSearchEventTriggerSource', args?.searchEventTriggerSource);
      const defaultArgs = {
        newCurrentPage: 1,
        fromPagination: false,
        removeSearchHeaderParam: false,
      };
      const newParams = {
        ...defaultArgs,
        ...args,
      };
      const { newCurrentPage, fromPagination, removeSearchHeaderParam } = newParams;
      if (!fromPagination) {
        commit('setNoResultSubsequentSearchCount', 0);
      }

      // when filter state changes always reset current page to 1
      // unless given a newCurrentPage arg
      // indicating that the only change is the current page number
      if (state.productsCurrentPage !== newCurrentPage) {
        commit('setProductsCurrentPage', newCurrentPage);
      }

      if (args?.type !== 'sortBy') {
        dispatch('search/setSearchParams', { type: SEARCH_TYPES.CATEGORY_SEARCH });
      }

      let url = stateToUrl(state);
      if (!removeSearchHeaderParam && router.currentRoute.query.search_header) {
        url += '&search_header=1';
      }
      // triggers setStateFromUrl
      router.push({
        path: `/browse${url}`,
      });

      return Promise.resolve();
    },
    productsChange({ state, commit, dispatch }, products) {
      if (products.length === 0) {
        commit('setNoResultSubsequentSearchCount', state.noResultSubsequentSearchCount + 1);

        // We perform subsequent search only twice to avoid infinite loop.
        if (state.noResultSubsequentSearchCount <= 2) {
          Vue.nextTick(() => {
            dispatch('fetch');
          });
        }
      }

      return Promise.resolve();
    },
  },
  getters: {
    ...mapSimpleGetters([
      'productsCurrentPage',
      'productsLastPage',
      'categoryFilters',
      'searchTags',
      'searchEventTriggerSource',
      'searchEventLocationSource',
      'userLoggedIn',
      'siteTotalProductsCount',
      'loginRegisterModalActive',
      'isFree',
      'sortBy',
      'dateAdded',
      'products',
      'loading',
      'noResultSubsequentSearchCount',
    ]),
    productsCount(state) {
      return Array.isArray(state.products) ? state.products.length : 0;
    },
    people() {
      return people;
    },
    fps() {
      return FPS_BY_SLUG;
    },
    orientations() {
      return ORIENTATION_BY_SLUG;
    },
    resolutions() {
      return RESOLUTION_BY_SLUG;
    },
    editorials() {
      return editorials;
    },
    apiUrl(state) {
      const request = stateToApiRequest(state);
      return stateToUrl(request);
    },
    singleCategory(state) {
      const selected = Object.values(state.categoryFilters).filter(category => category.selected);

      if (selected.length === 1) {
        return selected[0];
      }

      return undefined;
    },
    singleSubCategory(state, getters) {
      const category = getters.singleCategory;

      if (!category) {
        return undefined;
      }

      const selected = Object.values(category.subCategories).filter(
        subCategory => subCategory.selected,
      );

      if (selected.length === 1) {
        return selected[0];
      }

      return undefined;
    },
    dateAddedLabel(state) {
      return DATE_ADDED_HEADER_LABELS[state.dateAdded];
    },
    // getters that require arguments are prefixed with 'get'
    getCategoryVersion(state) {
      return state.categoryFilters.version;
    },
    getCategoryVersionOptions(state) {
      return CATEGORY_VERSION_BY_SLUG[state.categoryFilters.slug];
    },
    getSubCategoryCount(state) {
      const { subCategories } = state.categoryFilters;
      let count = 0;

      Object.values(subCategories).forEach(subCategory => {
        if (subCategory.selected) {
          count += 1;
        }
      });
      return count;
    },
  },
};
